import React from 'react'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { WalletCurrency } from '@/domain'
import { currencyUtils } from '@/utils'
import { getDisplayCurrencyCode } from '@/utils/getDisplayCurrencyCode'

const { formatFiatCurrency, currencyConditionals } = currencyUtils

interface AssetItemProps extends WalletCurrency {
  onPress: (currency: WalletCurrency) => void
  isFirstItem?: boolean
  isLastItem?: boolean
  haveOnlyOneItem?: boolean
}

export const AssetItem: React.FC<AssetItemProps> = ({
  onPress,
  isFirstItem = false,
  isLastItem = false,
  haveOnlyOneItem = false,
  ...item
}) => {
  const { isUsdt } = currencyConditionals(item.code || '')

  const formattedFiatAmount = formatFiatCurrency({
    value: item.fiatAmount || 0,
    fiatCode: item.fiatCurrency || 'USD',
    showDecimals: true,
  })

  const formattedCryptoAmount = isUsdt
    ? Number(item.amountWithoutNumberConversion || '0')?.toFixed(2)
    : item.amountWithoutNumberConversion || '0'

  const handlePress = () => {
    onPress(item)
  }

  // Border radius logic based on position in list
  const getBorderRadius = () => {
    if (haveOnlyOneItem) {
      return '24px' // Fully rounded for single item
    }
    if (isFirstItem) {
      return '24px 24px 0 0' // Only top corners rounded
    }
    if (isLastItem) {
      return '0 0 24px 24px' // Only bottom corners rounded
    }
    return '0' // No rounding for middle items
  }

  return (
    <BaseView
      row
      align="center"
      gap={16}
      px={16}
      py={16}
      bgColor="background.cardItemRoot"
      style={{
        borderRadius: getBorderRadius(),
        cursor: 'pointer',
      }}
      onClick={handlePress}
    >
      {/* Currency Icon */}
      <CardItem.CurrencyIcon
        uri={item.icon}
        bgColor={item.avatarBGRefColor || '#ccc'}
        size={52}
        fallbackText={item.code?.substring(0, 2) || '?'}
      />

      {/* Currency Info */}
      <BaseView flex={1} gap={2}>
        {/* Currency Name */}
        <BaseText fontSize={18} fontFamily="bold" color="label.default">
          {item.name || 'Unknown Currency'}
        </BaseText>
        {/* Currency Code */}
        <BaseText
          style={{ visibility: 'hidden' }}
          fontSize={14}
          fontFamily="regular"
          color="label.description"
        >
          {getDisplayCurrencyCode(
            item.displayCurrencyCode || item.code || 'N/A',
          )}
        </BaseText>
      </BaseView>

      {/* Amount Info */}
      <BaseView gap={2} align="flex-end">
        {/* Fiat Amount */}
        <BaseText fontSize={18} fontFamily="bold" color="label.default">
          {formattedFiatAmount}
        </BaseText>
        {/* Crypto Amount */}
        <BaseText fontSize={16} fontFamily="semibold" color="label.description">
          {formattedCryptoAmount}
        </BaseText>
      </BaseView>
    </BaseView>
  )
}
